<template>
  <div>
    <breadcrumb :items="breadcrumbs" />

    <v-container fluid>
      <div class="d-flex mb-6 align-center">
        <h2 class="font-weight-bold">{{ $t("carbonCredit.manageTrees.treeDetails") }}</h2>
        <v-spacer></v-spacer>
        <v-btn outlined class="mr-3 action-btn-outline" @click="goBack">{{ $t("carbonCredit.manageTrees.back") }}</v-btn>
        <v-btn color="primary" class="mr-3" @click="openHistory">{{ $t("carbonCredit.manageTrees.viewHistory") }}</v-btn>
        <v-btn color="primary" @click="provideUpdates">{{ $t("carbonCredit.manageTrees.provideUpdates") }}</v-btn>
      </div>

      <v-card elevation="0" class="pa-6 detail-card">
        <v-row v-if="loadingTree">
          <v-col cols="12" class="text-center py-12">
            <v-progress-circular indeterminate color="primary" />
          </v-col>
        </v-row>

        <template v-else>
          <v-row>
            <v-col
              v-for="(field, idx) in detailFields"
              :key="`field-${idx}`"
              cols="12"
              md="3"
              class="py-2"
            >
              <div class="detail-label">{{ field.label }}</div>
              <div class="detail-value">{{ field.value }}</div>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12" md="3">
              <div class="detail-label">{{ $t("carbonCredit.manageTrees.additionalNotes") }}</div>
              <div class="detail-value">
                {{ tree.additional_note || "-" }}
              </div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="detail-label">{{ $t("carbonCredit.manageTrees.programType") }}</div>
              <div class="detail-value">
                {{ programTypeText }}
              </div>
            </v-col>
          </v-row>

          <v-row class="mt-5">
            <v-col cols="12" md="12">
              <div class="detail-label mb-2">{{ $t("carbonCredit.manageTrees.treeImages") }}</div>
              <div class="d-flex flex-wrap">
                <div
                  v-for="(img, idx) in treeImages"
                  :key="`img-${idx}`"
                  class="image-wrap mr-3 mb-3"
                >
                  <v-img :src="img.url" width="180" height="140" class="rounded" cover @click="openImagePreview(img.url)" />
                  <div class="image-meta mt-2">
                    <div class="meta-line">{{ img.pictureTag || "-" }}</div>
                    <div class="meta-line">{{ img.pictureTimestamp || "-" }}</div>
                    <a
                      v-if="img.pictureLink"
                      :href="img.pictureLink"
                      class="meta-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ img.pictureLink }}
                    </a>
                    <div v-else class="meta-line">-</div>
                  </div>
                </div>
                <div v-if="!treeImages.length" class="detail-value">-</div>
              </div>
            </v-col>

            <v-col cols="12" md="12">
              <div class="detail-label mb-2">{{ $t("carbonCredit.manageTrees.treeQrCode") }}</div>
              <div class="d-flex align-end">
                <div class="qr-box">
                <div ref="qrCodeWrapper">
                  <qrcode :text="qrText" :size="118" />
                </div>
              </div>
              <v-btn
                  color="primary"
                  class="mt-3 ml-3"
                  @click="downloadQrCode"
                >
                  <v-icon left>mdi-download</v-icon>
                  Download QR
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-5" />

          <div class="detail-label mb-3">{{ $t("carbonCredit.manageTrees.treeAddedBy") }}</div>
          <v-row>
            <v-col cols="12" md="3">
              <div class="detail-label">{{ $t("carbonCredit.manageTrees.username") }}</div>
              <div class="detail-value">{{ tree.user?.fullName || "-" }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="detail-label">{{ $t("carbonCredit.user.email") }}</div>
              <div class="detail-value">{{ tree.user?.email || "-" }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="detail-label">{{ $t("carbonCredit.user.userRole") }}</div>
              <div class="detail-value">{{ userRoleText }}</div>
            </v-col>
            <v-col cols="12" md="3">
              <div class="detail-label">{{ $t("carbonCredit.user.userRegistrationDate") }}</div>
              <div class="detail-value">{{ formatDateTime(tree.created_at || tree.createdAt) }}</div>
            </v-col>
          </v-row>
        </template>
      </v-card>
    </v-container>

    <v-dialog v-model="imagePreviewDialog" max-width="760">
      <v-card>
        <v-card-title class="primary white--text">
          {{ $t("carbonCredit.manageTrees.treeImage") }}
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="closeImagePreview">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-5">
          <v-img
            :src="previewImageUrl"
            max-height="520"
            contain
            class="preview-image"
          />
        </v-card-text>

        <v-card-actions class="pb-4 pr-4">
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" @click="closeImagePreview">{{ $t("carbonCredit.cancel") }}</v-btn>
          <v-btn color="primary" :disabled="!previewImageUrl" @click="downloadPreviewImage">
           <v-icon left>mdi-download</v-icon> {{ $t("carbonCredit.manageTrees.downloadImage") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import CarbonCreditService from "@/_services/CarbonCreditService";
import DateMixin from "@/mixins/DateMixin";

export default {
  name: "ViewTree",
  mixins: [DateMixin],

  data() {
    return {
      loadingTree: false,
      tree: {},
      imagePreviewDialog: false,
      previewImageUrl: "",
      breadcrumbs: [
        { text: "Dashboard", disabled: false, href: "/dashboard", exact: true },
        { text: "Carbon Credit", disabled: false, href: "/carbon-credit/projects" },
        { text: "Manage Trees", disabled: false, href: "/carbon-credit/manage-trees" },
        { text: "Tree Details", disabled: true },
      ],
    };
  },

  computed: {
    treeId() {
      return this.$route.params.id;
    },
    detailFields() {
      return [
        { label: this.$t("carbonCredit.manageTrees.dimitraAssetId"), value: this.tree.id || "-" },
        { label: this.$t("carbonCredit.manageTrees.treeId"), value: this.tree.tree_id_by_user || "-" },
        { label: this.$t("carbonCredit.manageTrees.additionalId"), value: this.tree.additional_id || "-" },
        { label: this.$t("carbonCredit.user.farm"), value: this.tree.farm?.farmName || "-" },
        {
          label: this.$t("carbonCredit.manageTrees.zone"),
          value: this.tree.zone?.geofenceName || this.tree.zone_id || "-",
        },
        { label: this.$t("carbonCredit.manageTrees.gpsCoordinates"), value: this.gpsText },
        { label: this.$t("carbonCredit.manageTrees.altitude"), value: this.tree.altitude || "-" },
        { label: this.$t("carbonCredit.manageTrees.locationInformation"), value: this.tree.location_information || "-" },
        { label: this.$t("carbonCredit.manageTrees.seedlingName"), value: this.tree.seedling_name || "-" },
        { label: this.$t("carbonCredit.manageTrees.treeType"), value: this.tree.treeType?.name || "-" },
        { label: this.$t("carbonCredit.manageTrees.treeSpecies"), value: this.tree.treeSpecies?.name || "-" },
        {
          label: this.$t("carbonCredit.manageTrees.dateOfPlantation"),
          value: this.tree.date_planted ? this.formatDate(this.tree.date_planted).toUpperCase() : "-",
        },
        { label: this.$t("carbonCredit.manageTrees.ageOfTree"), value: this.ageOfTreeText },
        { label: this.$t("carbonCredit.manageTrees.healthCondition"), value: this.tree.healthCondition?.name || "-" },
      ];
    },
    gpsText() {
      if (this.tree.lat == null || this.tree.lon == null) return "-";
      return `${this.tree.lat}, ${this.tree.lon}`;
    },
    ageOfTreeText() {
      if (!this.tree.date_planted) return "-";
      const plantedAt = moment(this.tree.date_planted);
      if (!plantedAt.isValid()) return "-";
      const now = moment();
      if (plantedAt.isAfter(now)) return "-";

      const years = now.diff(plantedAt, "years");
      const afterYears = plantedAt.clone().add(years, "years");
      const months = now.diff(afterYears, "months");

      if (years <= 0 && months <= 0) return "0 Months";
      if (years > 0 && months > 0) {
        return `${years} Year${years === 1 ? "" : "s"} ${months} Month${months === 1 ? "" : "s"}`;
      }
      if (years > 0) return `${years} Year${years === 1 ? "" : "s"}`;
      return `${months} Month${months === 1 ? "" : "s"}`;
    },
    programTypeText() {
      if (!Array.isArray(this.tree.program_type) || !this.tree.program_type.length) return "-";
      return this.tree.program_type
        .map((item) => (typeof item === "object" ? item.name : item))
        .filter(Boolean)
        .join(", ") || "-";
    },
    treeImages() {
      const list = Array.isArray(this.tree.my_tree_images) ? this.tree.my_tree_images : [];
      return list
        .map((img) => ({
          url: img.file_url || img.fileUrl || img.url || img.location || "",
          pictureTag: img.picture_tag || img.pictureTag || img.name || "",
          pictureLink: img.picture_link || img.pictureLink || img.file_url || img.fileUrl || "",
          pictureTimestamp: this.formatPictureTimestamp(
            img.picture_date_stamp || img.pictureDateStamp,
            img.picture_time_stamp || img.pictureTimeStamp
          ),
        }))
        .filter((img) => !!img.url);
    },
    userRoleText() {
      const roleList = Array.isArray(this.tree?.user?.user_role_assoc)
        ? this.tree.user.user_role_assoc
        : [];
      const labels = roleList
        .map((role) => role?.name || role?.id || role?.role_type)
        .filter(Boolean);
      return labels.length ? labels.join(", ") : "-";
    },
    qrText() {
      return JSON.stringify({
        dimitraAssetId: String(this.tree.id || ""),
        treeId: String(this.tree.tree_id_by_user || ""),
        treeSpecies: String(this.tree.treeSpecies?.name || ""),
        timestamp: moment().toISOString(),
      });
    },
  },

  created() {
    this.fetchTree();
  },

  methods: {
    async fetchTree() {
      this.loadingTree = true;
      try {
        const res = await CarbonCreditService.getTreeById(this.treeId);
        this.tree = res?.data?.data || res?.data || res || {};
      } catch (err) {
        this.$notify({
          text: err?.response?.data?.message || "Failed to load tree details",
          type: "error",
        });
      } finally {
        this.loadingTree = false;
      }
    },
    goBack() {
      this.$router.push({ name: "manageTrees" });
    },
    openHistory() {
      this.$router.push({ name: "treeHistory", params: { id: this.treeId } });
    },
    provideUpdates() {
      this.$router.push({ name: "editTree", params: { id: this.treeId } });
    },
    formatDateTime(date) {
      if (!date) return "-";
      return moment(date).format("DD-MMM-YYYY | HH:mm:ss").toUpperCase();
    },
    formatPictureTimestamp(datePart, timePart) {
      if (!datePart && !timePart) return "";
      const normalizedDate = datePart ? this.formatDate(datePart).toUpperCase() : "";
      const normalizedTime = timePart ? String(timePart).slice(0, 8) : "";
      return [normalizedDate, normalizedTime].filter(Boolean).join(" | ");
    },
    openImagePreview(url) {
      if (!url) return;
      this.previewImageUrl = url;
      this.imagePreviewDialog = true;
    },
    closeImagePreview() {
      this.imagePreviewDialog = false;
      this.previewImageUrl = "";
    },
    downloadPreviewImage() {
      if (!this.previewImageUrl) return;
      const anchor = document.createElement("a");
      anchor.href = this.previewImageUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.download = `tree-image-${this.treeId}.jpg`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },
    downloadQrCode() {
      const wrapper = this.$refs.qrCodeWrapper;
      if (!wrapper) return;

      const canvasEl = wrapper.querySelector("canvas");
      const imgEl = wrapper.querySelector("img");
      const svgEl = wrapper.querySelector("svg");

      let dataUrl = "";

      if (canvasEl) {
        dataUrl = canvasEl.toDataURL("image/png");
      } else if (imgEl && imgEl.src) {
        dataUrl = imgEl.src;
      } else if (svgEl) {
        const svgString = new XMLSerializer().serializeToString(svgEl);
        dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
      }

      if (!dataUrl) return;

      const anchor = document.createElement("a");
      anchor.href = dataUrl;
      anchor.download = `tree-qr-${this.treeId}.png`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },
  },
};
</script>

<style scoped lang="scss">
.detail-card {
  border: 1px solid #efefef;
  border-radius: 8px;
}

.action-btn-outline {
  border-color: #00bd73 !important;
  color: #00bd73 !important;
}

.detail-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  word-break: break-word;
}

.image-wrap {
  width: 180px;
}

.image-wrap .v-image {
  border: 1px solid #ececec;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.image-meta {
  font-size: 14px;
  color: #222;
}

.meta-line {
  margin-bottom: 4px;
  word-break: break-word;
}

.meta-link {
  color: #2b2b2b;
  text-decoration: underline;
  word-break: break-word;
}

.qr-box {
  border: 1px dashed #cfd8dc;
  border-radius: 8px;
  padding: 12px;
  width: fit-content;
}
</style>
